'use client';

/**
 * Starter Prompts (Welcome Examples)
 *
 * Shows example prompts when user opens a new/empty chat.
 * Demonstrates AI capabilities and creates "wow effect" to drive subscriptions.
 * Disappears after first message is sent.
 */

import { Stethoscope } from 'lucide-react';
import React from 'react';
import { cn } from '../../utils/cn';
import { usePlatform } from '../../contexts/platform-context';
import { STARTER_PROMPTS } from '../prompts-list';

export type StarterPrompt = {
  icon: React.ElementType;
  title: string;
  description: string;
  prompt: string;
  gradient: string; // Tailwind gradient classes
};

type StarterPromptsProps = {
  onPromptClick: (prompt: string) => void;
  skillStarterPrompts?: Array<{
    id: string;
    title: string;
    description: string;
    prompt: string;
    gradient?: string | undefined;
  }>;
  activeSkillName?: string;
};

export const StarterPrompts: React.FC<StarterPromptsProps> = ({
  onPromptClick,
  skillStarterPrompts,
  activeSkillName,
}) => {
  const { isSidebar } = usePlatform();

  const prompts = skillStarterPrompts
    ? skillStarterPrompts.map(sp => ({
        icon: Stethoscope as React.ElementType,
        title: sp.title,
        description: sp.description,
        prompt: sp.prompt,
        gradient:
          sp.gradient ?? 'from-primary/10 to-primary/10 hover:from-primary/20 hover:to-primary/20',
      }))
    : STARTER_PROMPTS;

  const headerTitle = activeSkillName ? `${activeSkillName}` : 'How can I help you today?';

  const headerSubtitle = activeSkillName
    ? 'Select a starter prompt or ask your own question'
    : 'Ask me anything about medical billing, coding, or documentation';

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-start min-h-full px-2',
        isSidebar ? 'py-2' : 'py-1 sm:py-3'
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'text-center animate-in fade-in duration-500',
          isSidebar ? 'mb-4 mt-4' : 'mb-8 mt-8'
        )}
      >
        <h2
          className={cn(
            'font-semibold text-foreground mb-1',
            isSidebar ? 'text-base' : 'text-2xl mb-2'
          )}
        >
          {activeSkillName ? `🩺 ${headerTitle}` : '👋 Welcome to CTRL ENTER!'}
        </h2>
        <p className="text-xs text-muted-foreground">
          {isSidebar ? 'Try an example to get started' : headerSubtitle}
        </p>
      </div>

      {/* Prompt Cards */}
      <div
        className={cn(
          'w-full gap-2',
          isSidebar
            ? 'flex flex-col'
            : 'flex flex-col items-center sm:grid sm:grid-cols-2 sm:gap-4 sm:max-w-4xl px-1'
        )}
      >
        {prompts.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              onClick={() => onPromptClick(item.prompt)}
              className={cn(
                'group relative text-left',
                'glass-surface border border-border/50',
                'transition-all duration-300 ease-out',
                'hover:scale-[1.01] hover:shadow-lg',
                'active:scale-[0.99]',
                'animate-in fade-in-0 slide-in-from-bottom-4',
                'flex flex-col gap-1.5',
                isSidebar
                  ? 'w-full rounded-xl p-3'
                  : 'w-full max-w-[250px] sm:max-w-none rounded-xl sm:rounded-2xl p-3 sm:p-4 gap-2 sm:gap-3',
                item.gradient
              )}
              style={{
                animationDelay: `${index * 75}ms`,
                animationFillMode: 'backwards',
              }}
            >
              {/* Row 1: Icon + Title + Subtitle */}
              <div className="flex items-center gap-2.5">
                <div
                  className={cn(
                    'flex-shrink-0 rounded-lg bg-foreground/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300',
                    isSidebar ? 'w-7 h-7' : 'w-5 h-5 sm:w-10 sm:h-10 sm:rounded-xl'
                  )}
                >
                  <Icon
                    className={cn(
                      isSidebar ? 'w-3.5 h-3.5' : 'w-2.5 h-2.5 sm:w-5 sm:h-5',
                      'text-foreground'
                    )}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                    {item.title}
                  </h3>
                  <p className="text-xs text-muted-foreground line-clamp-1">{item.description}</p>
                </div>
              </div>

              {/* Row 2: Preview Text */}
              <p
                className={cn(
                  'text-xs text-muted-foreground/70 leading-relaxed',
                  isSidebar ? 'line-clamp-1' : 'hidden sm:block line-clamp-2'
                )}
              >
                &ldquo;{item.prompt.substring(0, isSidebar ? 60 : 80)}...&rdquo;
              </p>

              {/* Hover Gradient Border Effect */}
              <div className="absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </button>
          );
        })}
      </div>

      {/* Footer Hint */}
      <div
        className={cn(
          'text-center animate-in fade-in duration-700 delay-500',
          isSidebar ? 'mt-3' : 'mt-6'
        )}
      >
        <p className="text-xs text-muted-foreground/60">
          💡 Click any example above to try it instantly
        </p>
      </div>
    </div>
  );
};

export default StarterPrompts;
